/**
 * Big Two (Pusoy Dos / Deuces) — server-authoritative rules engine, 4 players, one deal per match.
 *
 * Rules implemented:
 *  - One 52-card deck dealt out, 13 each. Rank order is 3 (low) … K, A, 2 (high); suits rank diamonds < clubs <
 *    hearts < spades.
 *  - Plays are a single, a pair, three of a kind or a five-card hand. Five-card hands rank straight < flush <
 *    full house < four of a kind (+ any fifth card) < straight flush. A straight is five consecutive ranks from
 *    3-4-5-6-7 up to 10-J-Q-K-A; a two cannot be part of a straight (a simplification of the many house rules).
 *    Straights and straight flushes compare by their highest card (rank, then suit); flushes by suit first, then by
 *    their cards from the highest down; full houses by the triple; fours by the four.
 *  - The holder of the three of diamonds leads and the first play must include it.
 *  - Each next player must play the same number of cards that beats the last play, or pass. Having passed, a player
 *    sits out until the pile is cleared, which happens when everybody else has passed (the last player to play leads
 *    again — or the next player with cards if they went out).
 *  - A player who plays their last card is out. They finish in the order they went out; the game ends when one player
 *    is left, who comes last.
 */
import { buildDeck, parseCard, removeCards, type CardId } from '../../core/cards.js';
import { checkInt, mergeKnown } from '../../core/config.js';
import { shuffle } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  type EngineEvent,
  type GameEngine,
  type Standing,
} from '../../core/types.js';

export interface BigTwoConfig {
  clearDelayMs: number;
}

export type BigTwoPhase = 'PLAYING' | 'CLEAR' | 'MATCH_END';

export type BigTwoKind = 'single' | 'pair' | 'triple' | 'straight' | 'flush' | 'fullhouse' | 'four' | 'straightflush';

export interface BigTwoCombo {
  kind: BigTwoKind;
  size: number;
  /** Comparable strength: a higher value beats a lower one of the same size. */
  value: number;
}

export interface BigTwoPlay {
  seat: number;
  cards: CardId[];
  kind: BigTwoKind;
}

export interface BigTwoState {
  config: BigTwoConfig;
  players: string[];
  phase: BigTwoPhase;
  hands: CardId[][];
  /** Plays on the pile in order; the last one is the one to beat. */
  pile: BigTwoPlay[];
  /** Seats that have passed since the pile was started. */
  passed: boolean[];
  turn: number;
  /** Seat that made the last play on the pile. */
  lastPlayer: number;
  /** Seat that leads the next pile once the current one is cleared. */
  nextLeader: number;
  /** The pile that was just cleared, kept for the table to show while it is swept away. */
  lastPile: BigTwoPlay[];
  pileNo: number;
  plays: number;
  /** Seats in the order they went out. */
  out: number[];
}

export type BigTwoAction = { type: 'play'; cards: CardId[] } | { type: 'pass' };

export interface BigTwoLegal {
  /** Every card set the seat may play now. */
  sets: CardId[][];
  canPass: boolean;
}

export interface BigTwoView {
  gameType: 'BIG_TWO';
  config: BigTwoConfig;
  seat: number | null;
  phase: BigTwoPhase;
  hand: CardId[];
  /** Public: how many cards every seat still holds. */
  handCounts: number[];
  pile: BigTwoPlay[];
  passed: boolean[];
  turn: number;
  lastPlayer: number;
  pileNo: number;
  out: number[];
  /** The number of cards a follower must play (0 when leading). */
  need: number;
  /** True until the first card of the game is played: that play must include the three of diamonds. */
  firstPlay: boolean;
  totals: number[];
}

const DEFAULTS: BigTwoConfig = { clearDelayMs: 1400 };
const SEATS = 4;
const OPENING_CARD = '3D';

const SUIT_POWER: Record<string, number> = { D: 0, C: 1, H: 2, S: 3 };

/** Rank strength: three lowest, then up to the ace, with the two highest. */
export const bigTwoRankPower = (card: CardId): number => {
  const r = parseCard(card).rank;
  return r === 2 ? 15 : r;
};

/** Full card strength: rank first, then suit (diamonds < clubs < hearts < spades). */
export const bigTwoPower = (card: CardId): number => bigTwoRankPower(card) * 4 + (SUIT_POWER[parseCard(card).suit] ?? 0);

const FIVE_BASE = 10_000_000;
const FIVE_RANK: Record<string, number> = { straight: 1, flush: 2, fullhouse: 3, four: 4, straightflush: 5 };

/** What a set of cards is as a Big Two play, or null if it is not a legal combination. */
export function bigTwoCombo(cards: readonly CardId[]): BigTwoCombo | null {
  const n = cards.length;
  if (n === 0 || (n > 3 && n !== 5)) return null;
  const parsed = cards.map((c) => parseCard(c));
  const power = cards.map(bigTwoPower);
  const rankPowers = cards.map(bigTwoRankPower);
  if (n === 1) return { kind: 'single', size: 1, value: power[0]! };
  if (n <= 3) {
    if (new Set(rankPowers).size !== 1 || new Set(cards).size !== n) return null;
    return n === 2 ? { kind: 'pair', size: 2, value: Math.max(...power) } : { kind: 'triple', size: 3, value: rankPowers[0]! };
  }
  if (new Set(cards).size !== 5) return null;
  const suited = new Set(parsed.map((p) => p.suit)).size === 1;
  const ranks = parsed.map((p) => p.rank).sort((a, b) => a - b);
  const straight = !ranks.includes(2) && ranks.every((r, i) => i === 0 || r === ranks[i - 1]! + 1);
  const counts = new Map<number, number>();
  for (const r of rankPowers) counts.set(r, (counts.get(r) ?? 0) + 1);
  const sizes = [...counts.values()].sort((a, b) => b - a);
  const rankOfSize = (k: number) => [...counts.entries()].find(([, c]) => c === k)![0];
  const top = Math.max(...power);
  if (straight && suited) return { kind: 'straightflush', size: 5, value: FIVE_RANK.straightflush! * FIVE_BASE + top };
  if (sizes[0] === 4) return { kind: 'four', size: 5, value: FIVE_RANK.four! * FIVE_BASE + rankOfSize(4) };
  if (sizes[0] === 3 && sizes[1] === 2) return { kind: 'fullhouse', size: 5, value: FIVE_RANK.fullhouse! * FIVE_BASE + rankOfSize(3) };
  if (suited) {
    const desc = [...rankPowers].sort((a, b) => b - a);
    const key = desc.reduce((acc, p) => acc * 16 + p, 0);
    return { kind: 'flush', size: 5, value: FIVE_RANK.flush! * FIVE_BASE + (SUIT_POWER[parsed[0]!.suit] ?? 0) * 1_000_000 + key };
  }
  if (straight) return { kind: 'straight', size: 5, value: FIVE_RANK.straight! * FIVE_BASE + top };
  return null;
}

function combinations(cards: readonly CardId[], n: number): CardId[][] {
  if (n === 0) return [[]];
  const out: CardId[][] = [];
  cards.forEach((c, i) => {
    for (const rest of combinations(cards.slice(i + 1), n - 1)) out.push([c, ...rest]);
  });
  return out;
}

/** The play a follower has to beat. */
export function bigTwoTarget(pile: readonly BigTwoPlay[]): { need: number; value: number } {
  const top = pile.at(-1);
  if (!top) return { need: 0, value: -1 };
  return { need: top.cards.length, value: bigTwoCombo(top.cards)?.value ?? -1 };
}

/** Every set of cards a seat may play, and whether it may pass. `opening` forces the three of diamonds. */
export function bigTwoLegal(hand: readonly CardId[], pile: readonly BigTwoPlay[], opening = false): BigTwoLegal {
  const { need, value } = bigTwoTarget(pile);
  const sizes = need === 0 ? [1, 2, 3, 5] : [need];
  const sets: CardId[][] = [];
  for (const n of sizes) {
    if (n > hand.length) continue;
    for (const set of combinations(hand, n)) {
      const combo = bigTwoCombo(set);
      if (!combo || combo.value <= value) continue;
      if (opening && !set.includes(OPENING_CARD)) continue;
      sets.push(set);
    }
  }
  return { sets, canPass: need > 0 };
}

const sameSet = (a: readonly CardId[], b: readonly CardId[]) => a.length === b.length && a.every((c) => b.includes(c));

/** Seats still holding cards, clockwise from `from` (inclusive). */
function nextWithCards(hands: readonly (readonly CardId[])[], from: number): number {
  for (let i = 0; i < SEATS; i++) {
    const seat = (from + i) % SEATS;
    if (hands[seat]!.length > 0) return seat;
  }
  return from;
}

const isOpening = (state: BigTwoState) => state.plays === 0 && state.pile.length === 0;

function finish(state: BigTwoState, events: EngineEvent[]): void {
  state.phase = 'MATCH_END';
  const left = state.hands.map((h, s) => (h.length > 0 ? s : -1)).filter((s) => s >= 0);
  for (const s of left) if (!state.out.includes(s)) state.out.push(s);
  events.push({ type: 'matchEnd', standings: standings(state) });
}

function clearPile(state: BigTwoState, leader: number, events: EngineEvent[]): void {
  state.phase = 'CLEAR';
  state.lastPile = state.pile.map((p) => ({ seat: p.seat, cards: [...p.cards], kind: p.kind }));
  state.nextLeader = leader;
  state.turn = leader;
  events.push({ type: 'pileCleared', by: 'passes', leader });
}

export const bigTwoEngine: GameEngine<BigTwoConfig, BigTwoState, BigTwoAction, BigTwoView, BigTwoLegal> = {
  gameType: 'BIG_TWO',
  kind: 'match',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('clearDelayMs', c.clearDelayMs, 0, 10000);
    return c;
  },

  seatLimits: () => ({ min: SEATS, max: SEATS }),

  createState(config, seats, rng) {
    assertEngine(seats.length === SEATS, 'INVALID_CONFIG', 'Big Two needs exactly 4 seats');
    const players = Array<string>(SEATS).fill('');
    for (const s of seats) {
      assertEngine(s.seat >= 0 && s.seat < SEATS && !players[s.seat], 'SEAT_TAKEN', `Bad seat ${s.seat}`);
      players[s.seat] = s.playerId;
    }
    const deck = shuffle(buildDeck(), rng);
    const hands: CardId[][] = [[], [], [], []];
    deck.forEach((c, i) => hands[i % SEATS]!.push(c));
    const leader = Math.max(0, hands.findIndex((h) => h.includes(OPENING_CARD)));
    return {
      config,
      players,
      phase: 'PLAYING',
      hands: hands.map((h) => [...h].sort((a, b) => bigTwoPower(a) - bigTwoPower(b))),
      pile: [],
      passed: [false, false, false, false],
      turn: leader,
      lastPlayer: leader,
      nextLeader: leader,
      lastPile: [],
      pileNo: 1,
      plays: 0,
      out: [],
    };
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'pass') return { type: 'pass' };
    if (raw.type === 'play' && Array.isArray(raw.cards) && raw.cards.length >= 1 && raw.cards.length <= 5 && raw.cards.every((c) => typeof c === 'string' && c.length <= 4)) {
      return { type: 'play', cards: raw.cards as string[] };
    }
    throw new EngineError('INVALID_ACTION', 'Unknown Big Two action');
  },

  applyAction(state, seat, action) {
    assertEngine(state.phase !== 'MATCH_END', 'GAME_OVER', 'The game is over');
    assertEngine(state.phase === 'PLAYING' && seat === state.turn, 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];
    const hand = state.hands[seat]!;
    const opening = isOpening(state);
    const legal = bigTwoLegal(hand, state.pile, opening);

    if (action.type === 'pass') {
      assertEngine(legal.canPass, 'ILLEGAL_MOVE', 'You must lead a card');
      state.passed[seat] = true;
      events.push({ type: 'pass', seat });
    } else {
      assertEngine(new Set(action.cards).size === action.cards.length, 'INVALID_CARD', 'Duplicate card');
      assertEngine(action.cards.every((c) => hand.includes(c)), 'INVALID_CARD', 'Card not in hand');
      const combo = bigTwoCombo(action.cards);
      assertEngine(combo !== null, 'ILLEGAL_MOVE', 'That is not a Big Two combination');
      assertEngine(!opening || action.cards.includes(OPENING_CARD), 'ILLEGAL_MOVE', 'The first play must include the three of diamonds');
      assertEngine(legal.sets.some((s) => sameSet(s, action.cards)), 'ILLEGAL_MOVE', 'That play does not beat the pile');
      removeCards(hand, action.cards);
      state.pile.push({ seat, cards: [...action.cards], kind: combo!.kind });
      state.lastPlayer = seat;
      state.plays++;
      events.push({ type: 'cardsPlayed', seat, cards: [...action.cards], kind: combo!.kind });
      if (hand.length === 0) {
        state.out.push(seat);
        events.push({ type: 'out', seat, place: state.out.length });
      }
    }

    if (state.hands.filter((h) => h.length > 0).length <= 1) {
      finish(state, events);
      return events;
    }

    // Anyone besides the last player who still holds cards and has not passed may answer.
    for (let i = 1; i < SEATS; i++) {
      const s = (seat + i) % SEATS;
      if (s !== state.lastPlayer && state.hands[s]!.length > 0 && !state.passed[s]) {
        state.turn = s;
        return events;
      }
    }
    clearPile(state, nextWithCards(state.hands, state.lastPlayer), events);
    return events;
  },

  getActors(state) {
    return state.phase === 'PLAYING' ? [state.turn] : [];
  },

  getLegalActions(state, seat) {
    if (state.phase !== 'PLAYING' || seat !== state.turn) return { sets: [], canPass: false };
    return bigTwoLegal(state.hands[seat]!, state.pile, isOpening(state));
  },

  getTimeoutAction(state, seat) {
    const legal = bigTwoLegal(state.hands[seat]!, state.pile, isOpening(state));
    if (legal.canPass) return { type: 'pass' };
    const lowest = [...legal.sets].sort((a, b) => a.length - b.length || (bigTwoCombo(a)?.value ?? 0) - (bigTwoCombo(b)?.value ?? 0))[0]!;
    return { type: 'play', cards: lowest };
  },

  getView(state, seat) {
    const showing = state.phase === 'CLEAR' ? state.lastPile : state.pile;
    return {
      gameType: 'BIG_TWO',
      config: state.config,
      seat,
      phase: state.phase,
      hand: seat === null ? [] : [...state.hands[seat]!],
      handCounts: state.hands.map((h) => h.length),
      pile: showing.map((p) => ({ seat: p.seat, cards: [...p.cards], kind: p.kind })),
      passed: [...state.passed],
      turn: state.turn,
      lastPlayer: state.lastPlayer,
      pileNo: state.pileNo,
      out: [...state.out],
      need: bigTwoTarget(state.pile).need,
      firstPlay: isOpening(state),
      totals: state.hands.map((h) => h.length),
    };
  },

  getSystemStep(state) {
    return state.phase === 'CLEAR' ? { kind: 'clearPile', delayMs: state.config.clearDelayMs } : null;
  },

  runSystemStep(state) {
    const events: EngineEvent[] = [];
    if (state.phase !== 'CLEAR') return events;
    state.pile = [];
    state.passed = [false, false, false, false];
    state.phase = 'PLAYING';
    state.turn = state.nextLeader;
    state.lastPlayer = state.nextLeader;
    state.pileNo++;
    events.push({ type: 'pileStarted', leader: state.turn });
    return events;
  },

  isFinished: (state) => state.phase === 'MATCH_END',

  getStandings(state): Standing[] {
    return standings(state);
  },

  getRoundsCompleted: (state) => state.pileNo,
};

/** Places in the order players went out, then whoever is left last. */
function standings(state: BigTwoState): Standing[] {
  const rest = [0, 1, 2, 3].filter((s) => !state.out.includes(s));
  return [...state.out, ...rest].map((seat, i) => ({ seat, place: i + 1, score: 100 - i * 10 - state.hands[seat]!.length }));
}
